//React & Redux
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

//Internal Compoments
import TestFooter from "../../components/layouts/Footer/TestFooter";
import Header from "../../components/layouts/Header/Header";
import PageContainer from "../../components/layouts/Containers/PageContainer";
import SEO from "../../components/seo/SEO";

// External Components
// Sanity Client
import { client } from "../../services/client";

//CSS
import "./about.css";

function AboutScreen() {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [pageContent, setPageContent] = useState([]);
  const [isContentReady, setIsContentReady] = useState(false);
  const [latestContent, setLatestContent] = useState();
  const [nextContent, setNextContent] = useState();

  let slug = "about-nowasteddays";

  async function getPosts() {
    const posts =
      await client.fetch(` *[_type == "video" && title match "ABOUT_*"]{
  title,
  description,
  "videoUrl": videoFile.asset->url,
  "thumbnailUrl": thumbnail.asset->url
}`);
    return posts;
  }

  useEffect(() => {
    const fetchData = async () => {
      let blogPosts = [];
      try {
        const response = await getPosts();

        if (response) {
          response.sort((a, b) => a.title.slice(-1) - b.title.slice(-1));
          setPageContent(response);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const videoRefs = useRef([]);

  const handleFullscreen = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      video.requestFullscreen();
    }
  };

  return (
    <>
      <SEO
        title="About NoWastedDays"
        description="Our w.e.i.r.d world today is flooded with things that are out to capture your attention. As a result, it has never been easier to become detached from who you are and the things that matter particularly, specifically and authentically just to you. To push back against this tide is exactly why NoWastedDays.io exists.."
        canonicalUrl="https://nowasteddays.io/about-nowasteddays"
      />
      <Header variant="light" active={false} />
      <PageContainer className="aboutpage">
        <div className="tile missionScreenTile">
          <div className="missionScreenContent">
            <div className="missionScreenText">
              <div>
                <strong>Doing more of what you love.</strong>
              </div>
              <p>
                Our w.e.i.r.d world today is flooded with things that are out to
                capture your attention. As a result, it has never been easier to
                become detached from who you are and the things that matter
                particularly, specifically and authentically just to you.
              </p>
              <p>
                To push back against this tide is exactly why NoWastedDays.io
                exists.
              </p>
              <hr></hr>
              <p>
                NoWastedDays is the marriage of 3 previously independent and
                useful things; a mindfulness journal, a weekly planner and a
                data dashboard.
              </p>
              <p>
                Bringing these three familiar features of life together
                digitally means that you now have the space to create and
                capture your vision for the year (
                <span style={{ fontWeight: "bold" }}>figure a</span>), to
                distill this vision into your aims for the week (
                <span style={{ fontWeight: "bold" }}>figure b</span>) and then
                manifest those aims into a fantastic, primed plan for the day
                ahead (<span style={{ fontWeight: "bold" }}>figure c</span>
                ).
              </p>
              <p>
                Outside of a few prompts or nudges so you know what kind of
                information lives where, there no steers as to what your vision
                should be, what your plans should contain or what a great day
                should look like; only a smart, simple and safe space to capture
                whatever they are for you. Having them at your fingertips means
                you can plan with them in mind, act on them more of the time,
                update them whenever you need to and, in doing so, maximise the
                chance that you are doing something you love as much of the time
                as you can.
              </p>
              <div>
                <strong>Loving more of what you do.</strong>
              </div>
              <p>
                Life isn't always going to allow you to prioritise your best
                laid plans or be free from disruption. Sometimes it will throw
                you a curveball, chuck a hurdle in your way or spin on its axis
                leaving you dazed and confused. Dealing with these circumstances
                will make you stronger, will make you wiser, more compassionate
                - and this may mean you learn to love them over time - but
                without a little support it is easy to feel overwhelmed, bitter
                and defeated.
              </p>
              <p>
                NoWastedDays is designed to be there through these turbulent
                times. For example...
              </p>
              <ol>
                <li>
                  The first page in your journal helps you prime your mind for a
                  healthy, happy day so when challenges do arise, you can
                  approach them from a mental space grounded in gratitude,
                  healthy detachment and self-compassion.
                </li>
                <li>
                  The last page in your journal helps you reflect on the day -
                  crystalising its unique elements and detaching from the parts
                  you aren't keen to repeat - so you can start tomorrow
                  completely fresh; not as a continuation of from the previous
                  day (<span style={{ fontWeight: "bold" }}>figure d</span>).
                </li>
                <li>
                  You can dig into your insights page (
                  <span style={{ fontWeight: "bold" }}>figure e</span>). Here
                  you can find a bird's eye view of your journalling that
                  provides an interesting perspective on the days you've logged.
                  No goods or bads. No cringey advice about you should think or
                  do. Simply some data to be guided by, with a bit of AI support
                  on hand if you need help getting started.
                </li>
                <li>
                  The Art of Looking is a unique learning course designed to
                  help you feel at home with the data in your life - principally
                  the data you create about yourself in your journal that now
                  feeds your insights page.
                </li>
              </ol>

              <div>
                <strong>Safe, Smart and Simple</strong>
              </div>
              <p>These are the core principles of the platform technology.</p>
              <ul>
                <li>
                  <strong>Safe:</strong> Your data will never be sold, it will
                  always be encrypted and will only ever exist to serve one
                  person - and that is you.
                </li>
                <li>
                  <strong>Simple:</strong> It will always be simple to use and
                  simple to explain. However, simple doesn't mean simplistic
                  hence the final principle...
                </li>
                <li>
                  <strong>Smart:</strong> It will always be fuelled by
                  technology and philosophy in the name of genuinely and
                  demonstrably improving individual wellbeing. The design of the
                  platform, the unique interactive experiences and AI
                  integrations are all there to ensure you benefit from hundreds
                  of development hours, thousands of years of wisdom and
                  millions of parameters of AI computation without, potentially,
                  ever really knowing it; all you feel is the benefit.
                </li>
              </ul>
              <div>
                <strong>A New Kind of Platform</strong>
              </div>
              <p>
                NoWastedDays is a new kind of digital project that aims to
                demonstrate that there are ways of sharing and scaling
                technology so that they systematically deliver practical and
                psychological value to the people that matter most - the people
                who use the platform itself.
              </p>
              <p>
                Learn more about that <a href="/nowasteddays-commons">here</a>{" "}
                if you are interested.
              </p>
            </div>

            <div className="missionScreenMedia">
              {pageContent.map((video, index) => {
                let figure = ["a", "b", "c", "d", "e"];
                let caption = [
                  "The year map - home for your vision, values and more.",
                  "The week planner - easy drag and drop organisation with the dock helping you access your yearly essentials.",
                  "The daily journal - prime, plan and process your day.",
                  "Capture the best and detach from the worst.",
                  "The insights hub - interesting data and AI powered support.",
                ];

                return (
                  <div
                    key={video.videoUrl}
                    className="mediaPlaceholder"
                    onClick={() => handleFullscreen(index)}
                  >
                    <video
                      ref={(el) => (videoRefs.current[index] = el)}
                      src={video.videoUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{ width: "100%", display: "block" }}
                    />
                    <label htmlFor="yearScreenVideo">{`figure ${figure[index]}: ${caption[index]}`}</label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </PageContainer>
      <TestFooter />
    </>
  );
}

export default AboutScreen;
